import React, { useEffect, useState } from 'react';
import { CreditNoteEntry } from './CreditNoteEntry';
import { CreditNoteList } from './CreditNoteList';
import { CreditNoteDetail } from './CreditNoteDetail';
import { CreditNoteDashboard } from './CreditNoteDashboard';
// Define tax codes at component level so they're available throughout
const TAX_CODES = [{
  code: 'STD',
  name: 'Standard Rate',
  rate: 10
}, {
  code: 'RED',
  name: 'Reduced Rate',
  rate: 5
}, {
  code: 'ZERO',
  name: 'Zero Rate',
  rate: 0
}, {
  code: 'EXM',
  name: 'Exempt',
  rate: 0
}];
// Add sample credit notes data
const sampleCreditNotes = [{
  id: 'cn-001',
  creditNoteNumber: 'CN-2023-001',
  vendor: 'Office Depot',
  creditNoteDate: '2023-10-15',
  amount: 750.25,
  currency: 'USD',
  status: 'Draft',
  appliedAmount: 0,
  unappliedAmount: 750.25,
  linkedInvoices: [],
  notes: '',
  lines: [{
    description: 'Return of office supplies',
    quantity: 1,
    unitPrice: 682.05,
    amount: 750.25,
    taxCode: 'STD',
    taxRate: 10,
    taxAmount: 68.2,
    invoiceRef: '',
    invoiceLineRef: ''
  }]
}, {
  id: 'cn-002',
  creditNoteNumber: 'CN-2023-002',
  vendor: 'Dell Technologies',
  creditNoteDate: '2023-10-12',
  amount: 430.0,
  currency: 'USD',
  status: 'Approved',
  appliedAmount: 430.0,
  unappliedAmount: 0,
  linkedInvoices: [],
  notes: 'Credit for damaged laptop',
  lines: [{
    description: 'Damaged laptop return',
    quantity: 1,
    unitPrice: 390.91,
    amount: 430.0,
    taxCode: 'STD',
    taxRate: 10,
    taxAmount: 39.09,
    invoiceRef: 'INV-2023-002',
    invoiceLineRef: 'Line 1'
  }]
}];
export function CreditNoteManagement({
  locale = 'en-US',
  currentView = 'credit-note-dashboard'
}) {
  const [internalView, setInternalView] = useState(currentView);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  // Update internal view when currentView prop changes
  useEffect(() => {
    setInternalView(currentView);
  }, [currentView]);
  // Sample credit note data
  const [creditNotes, setCreditNotes] = useState(sampleCreditNotes);
  // Sample invoice data for linking to credit notes
  const [invoices, setInvoices] = useState([{
    id: 'inv-001',
    invoiceNumber: 'INV-2023-001',
    vendor: 'Office Depot',
    invoiceDate: '2023-09-15',
    dueDate: '2023-10-15',
    amount: 1250.75,
    currency: 'USD',
    status: 'Pending'
  }, {
    id: 'inv-002',
    invoiceNumber: 'INV-2023-002',
    vendor: 'Dell Technologies',
    invoiceDate: '2023-09-10',
    dueDate: '2023-10-10',
    amount: 5430.0,
    currency: 'USD',
    status: 'Approved'
  }, {
    id: 'inv-003',
    invoiceNumber: 'INV-2023-003',
    vendor: 'PrintWorks Inc.',
    invoiceDate: '2023-09-05',
    dueDate: '2023-10-05',
    amount: 3200.5,
    currency: 'USD',
    status: 'Paid'
  }]);
  const handleSaveCreditNote = (creditNoteData, isDraft) => {
    // Calculate tax amounts and line totals if not already calculated
    const updatedLines = creditNoteData.lines.map(line => {
      const subtotal = line.quantity * line.unitPrice;
      const taxRate = TAX_CODES.find(tax => tax.code === line.taxCode)?.rate || 0;
      const taxAmount = subtotal * taxRate / 100;
      return {
        ...line,
        taxRate,
        taxAmount,
        amount: subtotal + taxAmount
      };
    });
    const totalAmount = updatedLines.reduce((sum, line) => sum + line.amount, 0);
    const newCreditNote = {
      id: selectedCreditNote?.id || `cn-${creditNotes.length + 1}`,
      creditNoteNumber: creditNoteData.creditNoteNumber || `CN-2023-${creditNotes.length + 1}`,
      vendor: creditNoteData.vendor,
      creditNoteDate: creditNoteData.creditNoteDate,
      amount: totalAmount,
      currency: creditNoteData.currency || 'USD',
      status: isDraft ? 'Draft' : 'Pending',
      appliedAmount: creditNoteData.appliedAmount || 0,
      unappliedAmount: totalAmount - (creditNoteData.appliedAmount || 0),
      linkedInvoices: creditNoteData.linkedInvoices || [],
      notes: creditNoteData.notes || '',
      lines: updatedLines
    };
    if (selectedCreditNote) {
      // Update existing credit note
      setCreditNotes(creditNotes.map(cn => cn.id === selectedCreditNote.id ? newCreditNote : cn));
    } else {
      // Add new credit note
      setCreditNotes([...creditNotes, newCreditNote]);
    }
    setSelectedCreditNote(null);
    setInternalView('credit-note-list');
  };
  const handleEditCreditNote = creditNote => {
    setSelectedCreditNote(creditNote);
    setInternalView('credit-note-entry');
  };
  return <div data-id="element-3714">
      {internalView === 'credit-note-dashboard' && <CreditNoteDashboard creditNotes={creditNotes} setCurrentView={setInternalView} locale={locale} data-id="element-3715" />}
      {internalView === 'credit-note-entry' && <CreditNoteEntry onSave={handleSaveCreditNote} invoices={invoices} initialData={selectedCreditNote} locale={locale} taxCodes={TAX_CODES} data-id="element-3716" />}
      {internalView === 'credit-note-list' && <CreditNoteList creditNotes={creditNotes} setCurrentView={setInternalView} setSelectedCreditNote={setSelectedCreditNote} locale={locale} data-id="element-3717" />}
      {internalView === 'credit-note-details' && selectedCreditNote && <CreditNoteDetail creditNote={selectedCreditNote} onBack={() => setInternalView('credit-note-list')} onEdit={handleEditCreditNote} locale={locale} data-id="element-3718" />}
    </div>;
}